import {
  IGizmo,
  IGizmoAncestor,
  IGizmoCallbacks,
  IGizmoData,
  IGizmoEvent,
  IGizmoFeatures,
  IRootGizmoCallbacks,
  ISubscription,
} from '../../types';
import Gizmo from '../gizmo';
import { binderInit } from '../../binders';
import { Subject } from 'rxjs';
import { getSubscriptions } from '../utils';
import { Flux } from '../../flux';
import { Dao } from '../../dao';
import RootGizmo from '../root-gizmo';
import { FlowyFoundation } from '../../flowy';

interface IParams {
  gizmo: Gizmo;
  context: {
    config: IGizmo;
    callbacks: IGizmoCallbacks;
    rootGizmoCallbacks: IRootGizmoCallbacks;
    foundation?: FlowyFoundation;
    defaultFeatures: IGizmoFeatures;
    setData: (data?: IGizmoData) => Promise<void>;
    events: Subject<IGizmoEvent>;
    params: any;
    dao?: Dao;
    ancestors: IGizmoAncestor[];
    setFlux: (flux: Flux) => void;
    setGizmos: (gizmos: (Gizmo | RootGizmo)[]) => void;
  };
}

interface IReturn {
  features: IGizmoFeatures;
  subscriptions: ISubscription[];
  binder?: any;
}

function init({ gizmo, context }: IParams): Promise<IReturn> {
  return new Promise<IReturn>(async resolve => {
    const {
      config,
      callbacks,
      rootGizmoCallbacks,
      foundation,
      defaultFeatures,
      setData,
      events,
      params,
      dao,
      ancestors,
      setFlux,
      setGizmos,
    } = context;
    const { gizmos: gizmoConfigs } = config;
    const features = defaultFeatures;
    const gizmos: (Gizmo | RootGizmo)[] = [];
    let binder: any | undefined;
    let flux: Flux | undefined;

    const data = callbacks.getInfoCB({
      type: 'data',
      fid: config.fid,
    });
    if (data !== undefined) {
      setData(data);
    }

    // Subs
    const subscriptions = getSubscriptions({ config });
    if (
      subscriptions.length > 0 ||
      (config.ops?.section?.jumps &&
        Array.isArray(config.ops.section.jumps) &&
        config.ops.section.jumps.length > 0) ||
      config.ops?.localValidation?.flux ||
      config.ops?.phoneField?.verification?.nameFlux ||
      config.ops?.value?.flux ||
      config.ops?.button?.operation === 'download' ||
      config.label.includes('{{') ||
      config.ops?.search?.rootGizmoFid ||
      config.type === 'index'
    ) {
      callbacks.setSubscriptionsCB({
        subscriptions,
      });
      flux = new Flux({
        gizmoCallbacks: callbacks,
        dao,
        gizmo,
      });
      setFlux(flux);
    }

    if (config.label.includes('{{') && flux) {
      const label = await flux.interpolate({
        script: config.label,
      });
      if (label !== undefined) {
        features.label = label;
      }
    }

    // Set features.required flag from configuration
    if (config.ops?.required !== undefined) {
      features.required = config.ops.required.isRequired;

      if (config.ops.required.flux && flux) {
        const result = await flux.evaluate({
          script: config.ops.required.flux,
        });

        if (result !== undefined) {
          features.required = result;
        }
      }
    }
    if (config.ops?.display?.isDisplayed !== undefined) {
      features.display = config.ops.display.isDisplayed;

      if (config.ops.display.flux && flux) {
        const result = await flux.evaluate({
          script: config.ops.display.flux,
        });

        if (result !== undefined) {
          features.display = result;
        }
      }
    }
    if (config.ops?.editable?.isEditable !== undefined) {
      features.editable = config.ops.editable.isEditable;

      if (config.ops.editable.flux && flux) {
        const result = await flux.evaluate({
          script: config.ops.editable.flux,
        });

        if (result !== undefined) {
          features.editable = result;
        }
      }
    }

    if (params && params.m === 's') {
      features.editable = false;
    }

    if (config.ops?.help?.message) {
      features.help = {
        message: config.ops.help.message,
        type: config.ops.help.type || 'tooltip',
        position: config.ops.help.position || 'under_gizmo',
      };
    }

    if (config.ops?.displayLabel === false) {
      features.displayLabel = false;
    }

    if (gizmoConfigs) {
      ancestors.push({
        fid: config.fid,
        uid: config.uid,
      });

      for (let i = 0; i < gizmoConfigs.length; i++) {
        const gizmoConfig = gizmoConfigs[i];

        if (gizmoConfig.type === 'view' || gizmoConfig.type === 'form') {
          const rg = new RootGizmo({
            config: gizmoConfig,
            callbacks: rootGizmoCallbacks,
            foundation,
            params,
            platformCallbacks: foundation?.getPlatformCallbacks(),
            dao,
          });
          await rg.init();
          gizmos.push(rg);
        } else {
          const g = new Gizmo({
            config: gizmoConfig,
            callbacks,
            rootGizmoCallbacks,
            platformCallbacks: foundation?.getPlatformCallbacks(),
            foundation,
            events,
            params,
            ancestors,
            dao,
          });

          // If the parent gizmo is a row and it has columns configured it passes the
          // columns to the child gizmo
          if (config.type === 'row') {
            if (config.ops?.row?.columns) {
              g.setColumns(config.ops.row.columns);
            }
          }

          // If The parent Gizmo is a section we're going to set the features first and
          // last in section
          if (config.type === 'section') {
            if (i === 0) {
              g.setFeatures({
                singleFeatures: [{ path: 'firstInSection', value: true }],
              });
            }

            // TODO: we have to improve this filter, this cannot handle cases of nested Gizmos
            const gizmosWithoutButtons = gizmoConfigs.filter(
              g =>
                ![
                  'button',
                  'row',
                  'row_container',
                  'column',
                  'column_container',
                ].includes(g.type)
            );

            if (i === gizmosWithoutButtons.length - 1) {
              g.setFeatures({
                singleFeatures: [{ path: 'lastInSection', value: true }],
              });
            }
          }

          await g.init();

          gizmos.push(g);
        }
      }

      setGizmos(gizmos);
    }

    binder = binderInit(gizmo);
    if (binder) {
      await binder.init();
    }

    resolve({
      features,
      binder,
      subscriptions,
    });
  });
}

export default init;
